import { computed, reactive, ref } from 'vue'
import { defineStore } from 'pinia'
import { dialog, i18N, toast } from '@/common/util'
import categoriasService from '@/services/categorias.service'

import type { ListBasicType, CategoriaType, CategoriesBasicType, LoaderType } from '@/types/types'

export const useCategoriaStore = defineStore('categoria', () => {
  /**
   * States
   */
  const list = ref<CategoriaType[]>([])
  const basicList = ref<{
    files: ListBasicType[]
    courses: ListBasicType[]
    trails: ListBasicType[]
    assessments: ListBasicType[]
  }>({
    files: [],
    courses: [],
    trails: [],
    assessments: []
  })

  const status = reactive({
    list: 'initial' as LoaderType,
    user: 'initial' as LoaderType,
    create: 'initial' as LoaderType,
    update: 'initial' as LoaderType,
    remove: 'initial' as LoaderType,
    get: 'initial' as LoaderType,
    basicList: {
      all: 'initial' as LoaderType,
      files: 'initial' as LoaderType,
      courses: 'initial' as LoaderType,
      trails: 'initial' as LoaderType,
      assessments: 'initial' as LoaderType
    }
  })

  /**
   * Getters
   */
  const loading = computed(() => Object.keys(status).some(key => status[key] === 'loading'))
  const loadingBasicList = computed(() =>  Object.values(status.basicList).some(value => value === 'loading'))

  const lazyFilter = ref(null)
  const lazyBasicList = computed(() => {
    return (type?: 'files' | 'courses' | 'trails' | 'assessments' | 'all', skipOrigin?: boolean) => {
      if (!type) type = 'all'

      const fId = `type:${type};origin:${skipOrigin ? 'all' : 'Proprio'}`
      if (status.basicList[type] === 'initial' || lazyFilter.value !== fId) {
        lazyFilter.value = fId
        if (!skipOrigin) getBasicList({ origem: 'Proprio', type: type })
        else getBasicList({ type: type })
        return []
      }

      return basicList.value[type]
    }
  })

  // Formata a lista para o formato do select
  const listFormat = computed(() => list.value
    .filter(el => el.origem === 'Próprio') // Na listagem não pode exibir itens de distruibuidor
    .map((cat) => ({ value: cat.id, label: cat.nome }))
  )

  /**
   * Actions
   */
  const create = async (dataObj: CategoriaType) => {
    status.create = 'initial'
    try {
      const { data, success } = await categoriasService.post(null, dataObj)
      if (success) {
        toast.success({message: i18N('toasts.createSuccess')})
        getAll()
        emptyList()
      }

      status.create = 'success'
      return data as CategoriaType
    } catch {
      status.create = 'error'
    }
  }

  const update = async (dataObj: CategoriaType, id: string) => {
    status.update = 'loading'
    try {
      const { data, success } = await categoriasService.put(id, dataObj)
      if (success) {
        const value = list.value.find(el => el.id === data.id)
        value && Object.assign(value, data)
        toast.success({message: i18N('toasts.updateSuccess')})
        emptyList()
      }

      status.update = 'success'
      return data as CategoriaType
    } catch {
      status.update = 'error'
    }
  }

  const getAll = async (): Promise<void> => {
    status.list = 'loading'

    try {
      const { data } = await categoriasService.get()
      list.value = data as CategoriaType[]

      status.list = 'success'
    } catch {
      status.list = 'error'
    }
  }

  const remove = async (categoria: CategoriaType, force?: boolean) => {
    if(!force) 
      return dialog.open({
        title: i18N('feedback.attention'),
        message: i18N('dialogs.delete.message', { type: i18N('input.category').toLowerCase(), name: categoria.nome }),
        complete: () => remove(categoria, true)
      })

    status.remove = 'loading'

    try {
      const { success } = await categoriasService.delete(categoria.id)

      if (success) {
        toast.success({message: i18N('toasts.removeSuccess')})
        list.value.splice(list.value.findIndex(el => el.id === categoria.id), 1)
        emptyList()
      }

      status.remove = 'success'

      return success
    } catch {
      status.remove = 'error'
    }
  }

  const getById = async (id: string): Promise<CategoriaType> => {
    status.get = 'loading'

    try {
      const { data } = await categoriasService.get(id)
      status.get = 'success'

      return data as CategoriaType
    } catch {
      status.get = 'error'
    }
  }

  const emptyList = () => {
    Object.keys(status.basicList).forEach((key) => status.basicList[key] = 'initial')
    Object.keys(basicList.value).forEach((key) => basicList.value[key].splice(0))
  }

  const getBasicList = (params?: CategoriesBasicType) => {
    if (!params) params = {}
    if (!params?.type) params.type = 'all'

    status.basicList[params.type] = 'loading'

    return categoriasService.getListBasic({ ...params })
      .then(({ data }) => {
        basicList.value[params.type] = (data || []).map(c => ({ value: c.id, label: c.nome }))
        status.basicList[params.type] = 'success'

        return basicList.value[params.type]
      })
      .catch(() => { status.basicList[params.type] = 'error' })
  }

  return {
    list,
    status,
    basicList,
    // Getters
    loading,
    loadingBasicList,
    lazyBasicList,
    listFormat,
    // Actions
    create,
    update,
    getAll,
    getById,
    remove,
    getBasicList,
    getListBasic: getBasicList
  }
})

export default useCategoriaStore
